import React, { useEffect, useState } from 'react';
import { select } from 'redux-saga/effects';
import PropTypes from 'prop-types';
import * as I18n from 'circe/I18n';
import Grid from 'pencil/Grid';
import FileField from 'circe/forms/fields/FileField';
import SelectField from 'circe/forms/fields/SelectField';
import CheckboxField from 'circe/forms/fields/CheckboxField';
import NumberField from 'circe/forms/fields/NumberField';
import InputAdornment from 'pencil/InputAdornment';
import * as Validation from 'circe/forms/validation';
import { onboardingClient } from 'domain/HttpClients';
import { getTruckTypes } from 'domain/operations/drivers/create/api';
import FieldTitle from 'domain/operations/drivers/create/components/Form/Sections/FieldTitle';
import { isLicensePlate, isValidAddress } from 'domain/operations/drivers/create/utils';
import LicensePlateField from 'domain/operations/drivers/create/components/LicensePlate/LicensePlateField';
import { filterVehicleTypes } from 'domain/vehicle/utils';
import AutoComplete from 'domain/finance/fuse/components/AutoComplete';
import { getFleetByName } from 'domain/fleets/apis';
import TextField from 'circe/forms/fields/TextField';
import { OWNERSHIP_TYPES } from 'domain/vehicle/components/create/constants';

const TruckDetails = (props) => {
  const {
    name, change, dispatch, token, valuesSelector,
  } = props;
  const classes = {};
  const [trucks, setTrucks] = useState([]);
  const [isFetchingFleets, setFetchingFleets] = useState(false);
  const [fleets, setFleets] = useState([]);
  const [fleet, setFleet] = useState(null);
  // Get Trucks Types
  const getTrucks = async () => {
    try {
      const response = await getTruckTypes(token);
      return response;
    } catch (e) {
      console.error(e);
      return [];
    }
  };
  useEffect(() => {
    const getTrucksList = async () => {
      const result = await getTrucks();
      setTrucks(result);
    };

    getTrucksList();
  }, []);

  return (
    <>
      <LicensePlateField
        disabled={false}
        validate={[Validation.required, isLicensePlate]}
        name={I18n.str('license', 'License')}
        change={change}
        propertyName="truckDetails.license"
      />
      <AutoComplete
        name={`${name}fleetId`}
        loading={isFetchingFleets}
        options={fleets}
        label={I18n.str('chooseFleet', 'Choose Fleet')}
        value={fleet}
        onChange={(value) => {
          setFleet(value);
          dispatch(change('truckDetails.fleetId', value));
        }}
        onKeyUp={async (val) => {
          await setFetchingFleets(true);
          const results = await getFleetByName(val, valuesSelector('companyId'));
          await setFleets(results);
          await setFetchingFleets(false);
        }}
        isClearable
      />
      <SelectField
        name={`${name}ownerShipType`}
        label={I18n.str('ownershipType', 'Ownership Type')}
        options={OWNERSHIP_TYPES}
        validate={[Validation.required]}
      />
      <SelectField
        name={`${name}truckType`}
        label={I18n.str('truckType', 'Truck Type')}
        options={filterVehicleTypes(trucks)}
        validate={[Validation.required]}
        onChange={(e, value) => {
          const [truckInfo] = trucks.filter((truck) => truck.type === value);
          const { length, width, height } = truckInfo.capacity;
          const max_weight = truckInfo.maxWeight;
          const { palletNo, label } = truckInfo;
          const capacity = height * length * width;

          dispatch(change('truckDetails.details', truckInfo));
          dispatch(change('truckDetails.length', length));
          dispatch(change('truckDetails.width', width));
          dispatch(change('truckDetails.height', height));
          dispatch(change('truckDetails.maxWeight', max_weight));
          dispatch(change('truckDetails.palletNo', palletNo));
          dispatch(change('truckDetails.maxCapacity', capacity.toFixed(2)));
          dispatch(change('truckDetails.within', 1000));
          dispatch(change('truckDetails.equipmentId', label));
        }}
      />
      <TextField
        fullWidth
        name={`${name}equipmentId`}
        label={I18n.str('equipmentId', 'Equipment Id ( name )')}
        onChange={(e) => {
          dispatch(change('truckDetails.equipmentId', e.target.value));
        }}
      />
      <NumberField
        name={`${name}length`}
        type="number"
        label={I18n.str('length', 'Length')}
        validate={[Validation.required, Validation.number]}
        inputClassName={classes.numberInput}
        innerLabel
        fullWidth
        min={1}
        InputProps={{
          endAdornment: (
            <InputAdornment className={classes.inputAdornment} position="start">
              {I18n.str('m', 'm')}
            </InputAdornment>
          ),
        }}
      />

      <NumberField
        name={`${name}width`}
        type="number"
        label={I18n.str('width', 'Width')}
        inputClassName={classes.numberInput}
        validate={[Validation.required, Validation.number]}
        innerLabel
        fullWidth
        min={1}
        InputProps={{
          endAdornment: (
            <InputAdornment className={classes.inputAdornment} position="start">
              {I18n.str('m', 'm')}
            </InputAdornment>
          ),
        }}
      />

      <NumberField
        name={`${name}height`}
        type="number"
        label={I18n.str('height', 'Height')}
        validate={[Validation.required, Validation.number]}
        inputClassName={classes.numberInput}
        innerLabel
        fullWidth
        min={1}
        InputProps={{
          endAdornment: (
            <InputAdornment className={classes.inputAdornment} position="start">
              {I18n.str('m', 'm')}
            </InputAdornment>
          ),
        }}
      />

      <NumberField
        name={`${name}maxCapacity`}
        type="number"
        label={I18n.str('maxCapacity', 'Max. Capacity')}
        validate={[Validation.required, Validation.number]}
        inputClassName={classes.numberInput}
        innerLabel
        fullWidth
        min={1}
        InputProps={{
          endAdornment: (
            <InputAdornment className={classes.inputAdornment} position="start">
              {I18n.str('cbm', 'cbm')}
            </InputAdornment>
          ),
        }}
      />

      <NumberField
        name={`${name}maxWeight`}
        type="number"
        label={I18n.str('maxWeight', 'Max. Weight')}
        validate={[Validation.required, Validation.number]}
        inputClassName={classes.numberInput}
        innerLabel
        fullWidth
        min={1}
        InputProps={{
          endAdornment: (
            <InputAdornment className={classes.inputAdornment} position="start">
              {I18n.str('kg', 'kg')}
            </InputAdornment>
          ),
        }}
      />
      <NumberField
        name={`${name}palletNo`}
        type="number"
        label={I18n.str('palletNumber', 'Pallet Number')}
        validate={[Validation.required, Validation.number]}
        inputClassName={classes.numberInput}
        innerLabel
        disabled
        fullWidth
        min={1}
        InputProps={{
          endAdornment: (
            <InputAdornment className={classes.inputAdornment} position="start">
              {I18n.str('pallets', 'Pallets')}
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

TruckDetails.propTypes = {
  name: PropTypes.string,
};

TruckDetails.defaultProps = {
  name: '',
};

export default TruckDetails;
